import { MOCK_COURSES, type ICourseDay } from './courses';
import { getAllExercises, getExerciseById, type IExercise } from './exercises';
import { normalizeLibrarySelectionIds } from '@/lib/libraryReferenceMapping';

export interface TrainingExperienceSettings {
  sets: number;
  reps: string;
  restSec: number;
  tempo: string;
  intensity: '低' | '中' | '高';
}

/** Experience adjusts workload and control; it never changes the required training environment. */
export function getTrainingExperienceSettings(months: number): TrainingExperienceSettings {
  if (months < 6) return { sets: 2, reps: '10-12', restSec: 75, tempo: '下放 2 秒，平稳发力', intensity: '低' };
  if (months < 24) return { sets: 3, reps: '12-15', restSec: 60, tempo: '下放 3 秒，平稳发力', intensity: '中' };
  return { sets: 4, reps: '12-15', restSec: 60, tempo: '下放 3 秒，底部停顿 1 秒', intensity: '高' };
}

const HOME_EQUIPMENT = new Set(['dumbbell', 'bodyweight', 'resistance-band']);
const GYM_DEPENDENCY = /杠铃|绳索|史密斯|龙门架|拉力器|器械|机器|蝴蝶机|高位下拉|引体|悬垂|双杠|卧推|上斜|平板凳|健身凳/;

export function isHomeExercise(exercise: IExercise): boolean {
  return HOME_EQUIPMENT.has(exercise.equipment) && !GYM_DEPENDENCY.test([exercise.name, ...exercise.tips, ...(exercise.detailGuide || [])].join(' '));
}

export function filterHomeExerciseIds(ids: string[]): string[] {
  return normalizeLibrarySelectionIds(ids).filter(id => {
    const exercise = getExerciseById(id);
    return !!exercise && isHomeExercise(exercise);
  });
}

const HOME_MAIN_IDS = {
  push: ['home-dumbbell-pushup', 'home-seated-dumbbell-press', 'home-seated-behind-neck-tricep', 'home-bent-tricep-kickback'],
  pull: ['home-double-dumbbell-bent-row', 'home-seated-dumbbell-rear-fly', 'home-dumbbell-curl', 'home-side-plank-v2'],
  lower: ['home-goblet-squat', 'home-bulgarian-split-squat-v2', 'home-glute-bridge-v2', 'home-side-plank-v2'],
  upper: ['home-dumbbell-pushup', 'home-double-dumbbell-bent-row', 'home-seated-dumbbell-press', 'home-seated-dumbbell-rear-fly', 'home-dumbbell-curl', 'home-bent-tricep-kickback'],
  full: ['home-goblet-squat', 'home-dumbbell-pushup', 'home-double-dumbbell-bent-row', 'home-seated-dumbbell-press', 'home-glute-bridge-v2', 'home-side-plank-v2'],
  core: ['home-side-plank-v2', 'home-glute-bridge-v2', 'home-prone-back-extension', 'home-prone-y-raise'],
} as const;

type HomeFocus = keyof typeof HOME_MAIN_IDS;
const HOME_COURSE_IDS: Record<HomeFocus, string> = {
  push: 'home-push', pull: 'home-pull', lower: 'home-lower-body',
  upper: 'home-shoulder-arm', full: 'home-lower-body', core: 'home-lower-body',
};
const HOME_FOCUS_LABELS: Record<HomeFocus, string> = {
  push: '胸 · 肩 · 三头', pull: '背 · 二头 · 核心', lower: '腿 · 臀 · 核心',
  upper: '胸 · 背 · 肩 · 臂', full: '全身复合训练', core: '核心稳定性 · 功能训练',
};

export function getTrainingSessionMinutes(weeklyHours: number, frequency: '1-2' | '3-4' | '5-6', sessionMinutes?: number): number {
  const weeklySessions = frequency === '1-2' ? 2 : frequency === '5-6' ? 5 : 3;
  const minutes = sessionMinutes ?? weeklyHours * 60 / weeklySessions;
  return Math.min(120, Math.max(15, Math.round(Number.isFinite(minutes) ? minutes : 60)));
}

/** Use existing home demonstrations, with a distinct workout focus for each recommended strength day. */
export function getHomeTrainingDay(dayIndex: number, frequency: '1-2' | '3-4' | '5-6', durationMin: number) {
  const sequence: HomeFocus[] = frequency === '1-2' ? ['full', 'full']
    : frequency === '3-4' ? ['upper', 'lower', 'full'] : ['push', 'pull', 'lower', 'upper', 'core'];
  const focus = sequence[(dayIndex - 1) % sequence.length];
  const course = MOCK_COURSES.find(item => item.id === HOME_COURSE_IDS[focus])!;
  const sourceDay = course.days[0];
  const stretchIds = getAllExercises().filter(exercise => (exercise.isStretch || exercise.bodyPart === 'stretch') && isHomeExercise(exercise)).slice(0, 3).map(exercise => exercise.id);
  const day: ICourseDay = {
    ...sourceDay,
    focus: HOME_FOCUS_LABELS[focus],
    durationMin,
    warmupIds: filterHomeExerciseIds(['band-shoulder-warmup', 'home-worlds-greatest-stretch']),
    mainIds: filterHomeExerciseIds([...HOME_MAIN_IDS[focus]]),
    stretchIds,
  };
  return { course, day };
}

/** Kept as a public export for consumers of the former generator mapping. */
export const HOME_EXERCISE_GAP_LIST: { exercise: string; target: string; reason: string }[] = [];
